import type { RequiredGeneric } from '@pba/react-utils';
import { useTranslation } from 'react-i18next';
import { Col, Fieldset, Form, Input, Row, SelectInput } from 'ui';
import { countries, states } from 'common/utils';

export type AddressFormValues = {
  apt?: string;
  city?: string;
  country?: string;
  state?: string;
  street?: string;
  zip?: string;
};

type P = {
  isRequired?: boolean;
};

const AddressFieldset = <FormValues extends AddressFormValues = never>({
  isRequired,
}: RequiredGeneric<FormValues, P>) => {
  const { t: translationCountry } = useTranslation('translationCountries');
  const { t: translationState } = useTranslation('translationStates');

  return (
    <Fieldset legend={{ title: 'Home Address' }}>
      <Row gutter="xs">
        <Col span={12}>
          <Form.Item<AddressFormValues>
            label="Street"
            name="street"
            rules={[
              {
                required: isRequired,
              },
            ]}
          >
            <Input autoComplete="address-line1" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<AddressFormValues> label="Apartment" name="apt">
            <Input autoComplete="address-line2" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item<AddressFormValues>
            label="City"
            name="city"
            rules={[
              {
                required: isRequired,
              },
            ]}
          >
            <Input autoComplete="address-level2" />
          </Form.Item>
        </Col>
        <Form.Item<AddressFormValues> dependencies={['country']} noStyle>
          {({ getFieldValue }) =>
            getFieldValue('country') === 'US' ? (
              <Col span={24}>
                <SelectInput
                  input={{
                    getOptionLabel: (option) => translationState(option.translationKey),
                    getOptionValue: (option) => option.code,
                    options: states,
                    showSearch: true,
                  }}
                  item={{
                    label: 'State',
                    name: 'state',
                    rules: [
                      {
                        required: isRequired,
                      },
                    ],
                  }}
                />
              </Col>
            ) : null
          }
        </Form.Item>
        <Col span={24}>
          <Form.Item<AddressFormValues>
            label="ZIP code"
            name="zip"
            rules={[
              {
                required: isRequired,
              },
            ]}
          >
            <Input autoComplete="postal-code" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <SelectInput
            input={{
              getOptionLabel: (option) => translationCountry(option.translationKey),
              getOptionValue: (option) => option.code,
              options: countries,
              showSearch: { optionFilterProp: 'label' },
            }}
            item={{
              label: 'Country',
              name: 'country',
              rules: [
                {
                  required: isRequired,
                },
              ],
            }}
          />
        </Col>
      </Row>
    </Fieldset>
  );
};

export default AddressFieldset;
